/**
 * CandidatesPanel.tsx   (auto-promote variant)
 * ─────────────────────────────────────────────────────────────────────────────
 * The "Candidates" tab in the React support console.
 *
 * AUTO-PROMOTE MODE — what this UI does
 * ─────────────────────────────────────
 * In auto-promote mode, every discovered element ALREADY has a GUID by
 * the time the agent sees it. The tab's job becomes:
 *
 *   • Browse what's been discovered, grouped by route.
 *   • Ignore noise (transient spinners, one-shot elements).
 *   • Merge per-row instances into a canonical id (e.g. 20 grid rows → 1).
 *   • Rename labels (Promote with a new label re-runs the same allocation).
 *   • Copy a ready-to-paste TS snippet if the agent wants to formalise
 *     a discovered element in lp-element-guids.ts.
 *
 * There is no "Promote" gate any more — discovery itself is the promotion.
 * What used to be a queue of decisions is now a curatable list.
 *
 * Layout: identical to the previous version. Filter chips remain ("Pending"
 * still appears because Promote / Ignore / Merge edge cases can set it,
 * but in normal use it'll always be 0).
 */

import React, { useMemo, useState, useEffect } from 'react';
import { useCandidates, CandidateRecord, PromoteResponse } from '../../hooks/useCandidates';

interface CandidatesPanelProps {
  apiBaseUrl:     string;
  internalSecret: string;
  active:         boolean;
  onPromoted?:    (resp: PromoteResponse) => void;
}

type Filter = 'promoted' | 'ignored' | 'pending' | 'all';

export function CandidatesPanel({ apiBaseUrl, internalSecret, active, onPromoted }: CandidatesPanelProps) {

  const {
    records, loading, error, lastFetchedAt, inFlight,
    refresh, promote, ignore, merge,
    counts, byRoute,
  } = useCandidates({ apiBaseUrl, internalSecret, enabled: active });

  // Default to "promoted" since that's the normal state in auto-promote mode.
  const [filter,      setFilter]      = useState<Filter>('promoted');
  const [snippet,     setSnippet]     = useState<string>('');
  const [snippetGuid, setSnippetGuid] = useState<string>('');
  const [selected,    setSelected]    = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!snippet) return;
    const t = setTimeout(() => setSnippet(''), 30_000);
    return () => clearTimeout(t);
  }, [snippet]);

  const filteredByRoute = useMemo(() => {
    if (filter === 'all') return byRoute;
    const result: Record<string, CandidateRecord[]> = {};
    for (const [route, items] of Object.entries(byRoute)) {
      const kept = items.filter(r => r.status === filter);
      if (kept.length > 0) result[route] = kept;
    }
    return result;
  }, [byRoute, filter]);

  // ── Action handlers ────────────────────────────────────────────────
  const handleRename = async (rec: CandidateRecord) => {
    const userLabel = window.prompt(
      `Rename this element (the GUID stays the same):\n\nLabel:`,
      rec.label || rec.tag,
    );
    if (userLabel === null) return;
    const trimmed = userLabel.trim();
    if (!trimmed || trimmed === rec.label) return;
    const res = await promote(rec.candidateId, { label: trimmed });
    if (res) {
      setSnippet(res.tsSnippet);
      setSnippetGuid(res.guid);
      onPromoted?.(res);
    }
  };

  const handleCopySnippet = (rec: CandidateRecord) => {
    if (!rec.allocatedGuid) return;
    // Recompute snippet client-side so we don't need to round-trip.
    const constName = `LP_ELEM_${(rec.label || rec.tag || 'ITEM')
      .toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 40)}`;
    const ts = `export const ${constName}: LpElementMeta = { guid: '${rec.allocatedGuid}', label: '${rec.label.replace(/'/g, "\\'")}', notes: 'Discovered on ${rec.route}' };`;
    setSnippet(ts);
    setSnippetGuid(rec.allocatedGuid);
  };

  const handleIgnore = async (rec: CandidateRecord) => {
    if (!window.confirm(
      `Ignore "${rec.label || rec.candidateId}"?\n\n` +
      `The Angular client will stop reporting this candidate. The GUID stays allocated.`,
    )) return;
    await ignore(rec.candidateId);
  };

  const handleToggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleMergeSelected = async () => {
    if (selected.size < 2) { alert('Select at least 2 candidates to merge.'); return; }
    const ids = Array.from(selected);
    const canonical = ids[0];
    const others    = ids.slice(1);
    const canonicalLabel = records.find(r => r.candidateId === canonical)?.label ?? canonical;
    if (!window.confirm(
      `Merge ${others.length} candidate(s) into "${canonicalLabel}"?\n\n` +
      `They will share ONE GUID — useful for grid rows that represent the same element.`,
    )) return;
    const res = await merge(canonical, others);
    if (res) {
      setSnippet(res.tsSnippet);
      setSnippetGuid(res.guid);
      setSelected(new Set());
      onPromoted?.(res);
    }
  };

  const copySnippet = () => {
    navigator.clipboard?.writeText(snippet).then(
      () => alert('TS snippet copied. Paste into src/app/constants/lp-element-guids.ts to formalise.'),
      () => alert('Copy failed — select the text manually.'),
    );
  };

  // ── Rendering ──────────────────────────────────────────────────────
  if (!active) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* ── Header bar ──────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 16px', borderBottom: '1px solid var(--br-base)',
        background: 'var(--bg-base)', flexShrink: 0, fontFamily: 'var(--f-display)', fontSize: 12,
      }}>
        <FilterChip active={filter === 'promoted'} onClick={() => setFilter('promoted')} label="Auto-promoted" count={counts.promoted} color="green" />
        <FilterChip active={filter === 'ignored'}  onClick={() => setFilter('ignored')}  label="Ignored"       count={counts.ignored}  color="grey"  />
        {counts.pending > 0 && (
          <FilterChip active={filter === 'pending'} onClick={() => setFilter('pending')} label="Pending" count={counts.pending} color="amber" />
        )}
        <FilterChip active={filter === 'all'}      onClick={() => setFilter('all')}      label="All"           count={counts.total}    color="blue"  />

        <div style={{ flex: 1 }}/>

        {selected.size >= 2 && (
          <button onClick={handleMergeSelected}
            style={{
              padding: '5px 10px', fontSize: 11, fontWeight: 700, fontFamily: 'var(--f-display)',
              background: 'var(--amber)', color: '#1a1a1a', border: 'none', borderRadius: 4, cursor: 'pointer',
            }}>
            Merge {selected.size} →
          </button>
        )}

        <button onClick={() => refresh()} disabled={loading}
          style={{
            padding: '5px 10px', fontSize: 11, background: 'transparent',
            border: '1px solid var(--br-base)', borderRadius: 4, color: 'var(--tx-secondary)',
            cursor: loading ? 'wait' : 'pointer',
          }}>
          {loading ? '⟳ refreshing' : '⟳ refresh'}
        </button>

        {lastFetchedAt && (
          <span style={{ fontSize: 10, color: 'var(--tx-muted)', fontFamily: 'var(--f-mono)' }}>
            {lastFetchedAt.toLocaleTimeString()}
          </span>
        )}
      </div>

      {/* ── Mode banner ─────────────────────────────────────────────── */}
      <div style={{
        padding: '6px 16px', background: 'rgba(46,160,67,0.08)',
        borderBottom: '1px solid rgba(46,160,67,0.25)', fontSize: 10.5,
        color: '#2EA043', fontFamily: 'var(--f-mono)', flexShrink: 0,
      }}>
        🔭 Auto-promote mode — every discovered element gets a GUID on first sight. Use Ignore to hide noise; use Merge to combine grid-row instances.
      </div>

      {/* ── Promote snippet banner ────────────────────────────────── */}
      {snippet && (
        <div style={{
          padding: '10px 16px', background: 'rgba(46,160,67,0.12)',
          borderBottom: '1px solid rgba(46,160,67,0.4)', display: 'flex',
          alignItems: 'flex-start', gap: 12, flexShrink: 0,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#2EA043', marginBottom: 4 }}>
              ✓ GUID: <code style={{ fontFamily: 'var(--f-mono)' }}>{snippetGuid}</code>
            </div>
            <pre style={{
              margin: 0, fontFamily: 'var(--f-mono)', fontSize: 11, color: 'var(--tx-secondary)',
              background: 'var(--bg-base)', padding: '8px 10px', borderRadius: 4,
              border: '1px solid var(--br-base)', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
              maxHeight: 80, overflow: 'auto',
            }}>{snippet}</pre>
            <div style={{ fontSize: 10, color: 'var(--tx-muted)', marginTop: 4 }}>
              Optional: paste into <code>src/app/constants/lp-element-guids.ts</code> to formalise this element.
            </div>
          </div>
          <button onClick={copySnippet}
            style={{
              padding: '5px 10px', fontSize: 11, background: '#2EA043', color: '#fff',
              border: 'none', borderRadius: 4, cursor: 'pointer', flexShrink: 0,
            }}>
            Copy
          </button>
          <button onClick={() => setSnippet('')}
            style={{
              padding: '5px 10px', fontSize: 11, background: 'transparent', color: 'var(--tx-muted)',
              border: '1px solid var(--br-base)', borderRadius: 4, cursor: 'pointer', flexShrink: 0,
            }}>
            Dismiss
          </button>
        </div>
      )}

      {error && (
        <div style={{
          padding: '8px 16px', background: 'rgba(244,63,94,0.12)', color: '#F43F5E',
          borderBottom: '1px solid rgba(244,63,94,0.4)', fontSize: 11, flexShrink: 0,
        }}>
          ⚠ {error}
        </div>
      )}

      {/* ── Body — list of candidates by route ─────────────────────── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
        {Object.keys(filteredByRoute).length === 0 && !loading && (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--tx-muted)', fontSize: 12 }}>
            {records.length === 0
              ? 'No candidates yet. Turn on 🔭 in the Angular app and navigate around.'
              : `No ${filter} candidates.`}
          </div>
        )}

        {Object.entries(filteredByRoute).map(([route, items]) => (
          <div key={route} style={{ marginBottom: 10 }}>
            <div style={{
              padding: '6px 16px', background: 'var(--bg-base)',
              borderTop: '1px solid var(--br-base)', borderBottom: '1px solid var(--br-base)',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <code style={{ fontFamily: 'var(--f-mono)', fontSize: 11, color: 'var(--tx-secondary)' }}>
                {route}
              </code>
              <span style={{ fontSize: 10, color: 'var(--tx-muted)' }}>
                {items.length} candidate{items.length === 1 ? '' : 's'}
              </span>
            </div>

            {items.map(rec => (
              <CandidateRow
                key={rec.candidateId}
                rec={rec}
                isSelected={selected.has(rec.candidateId)}
                isInFlight={inFlight.has(rec.candidateId)}
                onToggleSelect={() => handleToggleSelect(rec.candidateId)}
                onRename={() => handleRename(rec)}
                onCopySnippet={() => handleCopySnippet(rec)}
                onIgnore={() => handleIgnore(rec)}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function FilterChip({ active, onClick, label, count, color }: {
  active: boolean; onClick: () => void; label: string; count: number;
  color: 'amber' | 'green' | 'grey' | 'blue';
}) {
  const colorMap: Record<typeof color, string> = {
    amber: '#F5A623', green: '#2EA043', grey: '#6B7280', blue: '#009EDB',
  };
  return (
    <button onClick={onClick}
      style={{
        padding: '4px 10px', fontSize: 11, fontWeight: 600, fontFamily: 'var(--f-display)',
        background: active ? colorMap[color] : 'transparent',
        color:      active ? '#fff' : 'var(--tx-secondary)',
        border:    `1px solid ${active ? colorMap[color] : 'var(--br-base)'}`,
        borderRadius: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
      }}>
      {label}
      <span style={{
        padding: '0 5px', borderRadius: 6, fontSize: 10, fontFamily: 'var(--f-mono)',
        background: active ? 'rgba(255,255,255,0.25)' : 'var(--bg-raised)',
      }}>{count}</span>
    </button>
  );
}

function CandidateRow({ rec, isSelected, isInFlight,
                        onToggleSelect, onRename, onCopySnippet, onIgnore }: {
  rec:            CandidateRecord;
  isSelected:     boolean;
  isInFlight:     boolean;
  onToggleSelect: () => void;
  onRename:       () => void;
  onCopySnippet:  () => void;
  onIgnore:       () => void;
}) {
  const dimmed = rec.status === 'ignored';
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '8px 16px', borderBottom: '1px solid rgba(255,255,255,0.03)',
      opacity: dimmed ? 0.45 : 1,
      background: isSelected ? 'rgba(245,166,35,0.08)' : 'transparent',
    }}>
      {/* Multi-select checkbox (for Merge) — promoted rows only */}
      {rec.status === 'promoted' && (
        <input type="checkbox" checked={isSelected} onChange={onToggleSelect}
          style={{ accentColor: 'var(--amber)', cursor: 'pointer' }}/>
      )}

      {/* Label + meta */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--tx-primary)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {rec.label || <em style={{ color: 'var(--tx-muted)' }}>(no label)</em>}
        </div>
        <div style={{ fontSize: 10, color: 'var(--tx-muted)', fontFamily: 'var(--f-mono)',
          display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 2 }}>
          <span title="HTML tag">tag={rec.tag}</span>
          <span title="Times seen">{rec.occurrences}×</span>
          {rec.suggestedParentId && (
            <span title="DOM ancestor with a known GUID">parent={rec.suggestedParentId.slice(0, 8)}…</span>
          )}
          {rec.allocatedGuid && (
            <span title="Allocated GUID" style={{ color: '#2EA043' }}>
              guid={rec.allocatedGuid.slice(0, 8)}…
            </span>
          )}
          {rec.ancestorChain.length > 0 && (
            <span title="DOM ancestor chain" style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 300 }}>
              chain={rec.ancestorChain.slice(0, 3).join(' → ')}
            </span>
          )}
        </div>
      </div>

      <StatusBadge status={rec.status}/>

      {/* Promoted rows: rename / copy-snippet / ignore */}
      {rec.status === 'promoted' && (
        <>
          <button onClick={onRename} disabled={isInFlight}
            style={{
              padding: '4px 10px', fontSize: 11, background: 'transparent',
              color: 'var(--tx-secondary)', border: '1px solid var(--br-base)',
              borderRadius: 4, cursor: isInFlight ? 'wait' : 'pointer',
            }}>
            Rename
          </button>
          <button onClick={onCopySnippet}
            style={{
              padding: '4px 10px', fontSize: 11, background: 'transparent',
              color: 'var(--tx-secondary)', border: '1px solid var(--br-base)',
              borderRadius: 4, cursor: 'pointer',
            }}
            title="Copy a ready-to-paste TypeScript constant for lp-element-guids.ts">
            Copy snippet
          </button>
          <button onClick={onIgnore} disabled={isInFlight}
            style={{
              padding: '4px 10px', fontSize: 11, background: 'transparent',
              color: 'var(--tx-muted)', border: '1px solid var(--br-base)',
              borderRadius: 4, cursor: isInFlight ? 'wait' : 'pointer',
            }}>
            Ignore
          </button>
        </>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: 'pending' | 'promoted' | 'ignored' }) {
  const map = {
    pending:  { color: '#F5A623', label: 'PENDING'  },
    promoted: { color: '#2EA043', label: 'PROMOTED' },
    ignored:  { color: '#6B7280', label: 'IGNORED'  },
  } as const;
  const { color, label } = map[status];
  return (
    <span style={{
      padding: '2px 7px', fontSize: 9.5, fontWeight: 700, fontFamily: 'var(--f-mono)',
      color: '#fff', background: color, borderRadius: 8, letterSpacing: 0.4,
    }}>{label}</span>
  );
}
